import { DownloadSimpleIcon, EyeIcon, MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { useExportMaintenanceCsv } from '@/api/maintenance/export-maintenance-csv'
import { useGetMaintenanceCountsByClient } from '@/api/maintenance/get-maintenance-counts-by-client'
import {
  GET_MAINTENANCE_REQUESTS_BASE_QUERY_KEY,
  useGetMaintenanceRequests,
} from '@/api/maintenance/get-maintenance-requests'
import { useGetYardiPropertyIds } from '@/api/maintenance/get-yardi-property-ids'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import MaintenanceRequestPreview from '@/components/Maintenance/MaintenanceRequestPreview/MaintenanceRequestPreview'
import RequestMaintenanceForm from '@/components/Maintenance/RequestMaintenanceForm/RequestMaintenanceForm'
import ModalContainer from '@/components/ModalContainer/ModelContainer'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { StatusTabs } from '@/components/ui/status-tabs'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models'
import { MaintenanceRequest } from '@/models/features'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectRequest, setSelectedRequest } from '@/reducers/maintenanceReducer'
import { buildCsvFilename, downloadCsv, getApiErrorMessage } from '@/utils'

import {
  ADDITIONAL_FILTERS_DROPDOWN_WIDTH,
  getMaintenanceColumns,
  getMaintenancePriorityOptions,
  initialMaintenanceFilters,
  MAINTENANCE_DASHBOARD_ID,
  maintenanceFiltersSchema,
} from './maintenance-dashboard.constants'
import { MaintenanceDashboardProps } from './maintenance-dashboard.types'
import {
  getMaintenanceAllCount,
  getMaintenancePmsType,
  getMaintenanceStatusCounts,
  getMaintenanceStatusTabs,
  isPmsIntegratedBuilding,
  MaintenanceStatusFilter,
} from './maintenance-dashboard.utils'

export const MaintenanceDashboard = ({ resident, building }: MaintenanceDashboardProps = {}) => {
  const { t } = useTranslation(['maintenance'])
  const dispatch = useDispatch()
  const selectedRequest = useSelector(selectRequest) as MaintenanceRequest | null

  const [isShowAddRequest, setShowAddRequest] = useState(false)
  const [residentsSearch, setResidentsSearch] = useState('')

  const residentId = resident?.id
  const globalBuilding = useSelector(selectActiveBuilding)
  const selectedBuilding = building ?? globalBuilding
  const buildingId = selectedBuilding?.id
  const timezone = selectedBuilding?.timezone || 'America/Chicago'
  const isYardiProperty = !!(
    selectedBuilding?.yardiManagementId && selectedBuilding?.yardiPropertyId
  )
  // Note: Priority is a PMS-wide concept (Yardi, Entrata, RealPage, Resman, RentManager) - see
  // isPmsIntegratedBuilding. The Yardi-specific "phase" filter below stays gated on
  // isYardiProperty since it calls a Yardi-only endpoint (useGetYardiPropertyIds).
  const isPmsIntegrated = isPmsIntegratedBuilding(selectedBuilding)
  const pmsType = getMaintenancePmsType(selectedBuilding)

  const searchParams = useSearchParams({
    initialState: initialMaintenanceFilters,
    schema: maintenanceFiltersSchema,
  })

  const filters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: initialMaintenanceFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const status = filters.getFilter('status') ?? 'all'
  const statusParam = status === 'all' ? [] : [status]
  const priority = filters.getFilter('priority')
  const yardiPropertyId = filters.getFilter('yardiPropertyId')
  const search = filters.getFilter('search')
  const fromDateFilter = filters.getFilter('fromDate')
  const toDateFilter = filters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]
  const clientId = residentId ? [residentId] : filters.getFilter('clientId')
  const sortOrder = filters.getFilter('sortOrder')

  const queryParams = {
    buildingId,
    clientId,
    dateFrom: fromDateFilter,
    dateTo: toDateFilter,
    limit: filters.getFilter('limit'),
    priority: isPmsIntegrated ? priority : undefined,
    search,
    skip: filters.getFilter('skip'),
    status: statusParam,
    updatedAtSort: sortOrder ? (sortOrder.toUpperCase() as 'ASC' | 'DESC') : undefined,
    yardiPropertyId: isYardiProperty ? yardiPropertyId : undefined,
  }

  const {
    data,
    isFetching: isRequestsFetching,
    error,
    refetch,
  } = useGetMaintenanceRequests(queryParams)

  const requests = data?.maintenanceRequests ?? []
  const allCount = getMaintenanceAllCount(data)

  const residentsOptions = useGetMaintenanceCountsByClient({
    buildingId,
    dateFrom: fromDateFilter,
    dateTo: toDateFilter,
    limit: 25,
    priority: isPmsIntegrated ? priority : undefined,
    search: residentsSearch,
    status: statusParam,
    yardiPropertyId: isYardiProperty ? yardiPropertyId : undefined,
  })

  const { data: yardiPropertyIdsData } = useGetYardiPropertyIds(buildingId, isYardiProperty)
  const yardiPropertyOptions = (yardiPropertyIdsData ?? []).map(({ yardiPropertyId: id }) => ({
    label: id,
    value: id,
  }))

  const { mutateAsync: exportCsv, isPending: isExporting } = useExportMaintenanceCsv()

  const createdByColumnSettings = usePersonColumnSettings({
    column: 'createdBy',
    dashboard: 'Maintenance',
    fieldType: 'mixed',
    displays: requests.map((request) => request.createdByDisplay),
    rowsQueryKey: [GET_MAINTENANCE_REQUESTS_BASE_QUERY_KEY],
  })
  const assigneeColumnSettings = usePersonColumnSettings({
    column: 'assignee',
    dashboard: 'Maintenance',
    fieldType: 'staff',
    displays: requests.map((request) => request.assigneeDisplay),
    rowsQueryKey: [GET_MAINTENANCE_REQUESTS_BASE_QUERY_KEY],
  })

  const columns = getMaintenanceColumns({
    assigneeColumnSettings,
    createdByColumnSettings,
    pmsType,
    t,
    timezone,
  })

  const maintenancePriorityOptions = getMaintenancePriorityOptions(t)

  const showPriorityFilter = isPmsIntegrated
  const showYardiPropertyFilter = isYardiProperty && !!yardiPropertyOptions.length
  const showResidentFilter = !residentId
  // Note: TableGroup shows its filters toggle whenever `filters` is set, so pass nothing when none
  // of them apply (e.g. Resident Profile on a non-PMS building) instead of an empty fragment.
  const hasFilters = showPriorityFilter || showYardiPropertyFilter || showResidentFilter

  const statusCounts = getMaintenanceStatusCounts(data)
  const statusTabsOptions = getMaintenanceStatusTabs(t, statusCounts, allCount)

  const handleTabChange = (value: MaintenanceStatusFilter) => {
    filters.setFilter('status', value)
  }

  const handlePriorityChange = (values: (typeof maintenancePriorityOptions)[number]['value'][]) => {
    filters.setFilter('priority', values)
  }

  const handleYardiPropertyChange = (values: string[]) => {
    filters.setFilter('yardiPropertyId', values)
  }

  const handleResidentChange = (residentIds: number[]) => {
    filters.setFilter('clientId', residentIds)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    filters.setFilter('search', e.target.value)
  }

  const handleDateChange = (range: DateRange) => {
    filters.setFilters({ fromDate: range[0]?.getTime(), toDate: range[1]?.getTime() })
  }

  const handleSort = (sortBy: string | null, order: 'asc' | 'desc' | null) => {
    filters.setFilters({ sortBy: sortBy ?? undefined, sortOrder: order ?? undefined })
  }

  const rowActions: RowAction<MaintenanceRequest>[] = [
    {
      icon: EyeIcon,
      label: t('maintenance:requests.rowActions.view'),
      onClick: (row) => dispatch(setSelectedRequest(row)),
      primary: true,
    },
  ]

  const handleDownloadCSV = async () => {
    if (!buildingId) return

    try {
      const blob = await exportCsv({ ...queryParams, buildingId })
      downloadCsv({
        blob,
        filename: buildCsvFilename({
          building: selectedBuilding?.name,
          dateRange,
          page: 'maintenance',
          timezone,
        }),
      })
    } catch (downloadError) {
      toastFactory.error(getApiErrorMessage(downloadError))
    }
  }

  const handleCloseRequest = () => {
    dispatch(setSelectedRequest(null))
    void refetch()
  }

  if (selectedRequest) {
    return (
      <MaintenanceRequestPreview
        data={selectedRequest}
        buildingData={selectedBuilding}
        handleClose={handleCloseRequest}
      />
    )
  }

  return (
    <>
      {/* Note: column choices are saved per PMS (EL-15257) - a fresh key also discards every
          view saved before PMS defaults existed. Keyed on `pmsType` so switching to a building
          with another PMS remounts and re-reads that PMS's saved view; buildings sharing a PMS
          keep the same one. */}
      <TableGroup
        key={pmsType}
        id={MAINTENANCE_DASHBOARD_ID}
        columnsStorageKey={`${MAINTENANCE_DASHBOARD_ID}-${pmsType}`}
        columns={columns}
        data={requests}
        error={error}
        loading={isRequestsFetching}
        getRowKey={(request) => request.id}
        rowActions={rowActions}
        mode="regular"
        title={
          residentId && (
            <Typography as="h2" size="heading-small" color="black800">
              {t('maintenance:page.title')}
            </Typography>
          )
        }
        titleActions={
          residentId && (
            <Button size="small" onClick={() => setShowAddRequest(true)}>
              {t('maintenance:toolbar.addRequest')}
              <Icon icon={PlusIcon} />
            </Button>
          )
        }
        emptyNoFilters={{
          description: t('maintenance:requests.list.empty.description'),
          title: t('maintenance:requests.list.empty.title'),
        }}
        filters={
          hasFilters && (
            <>
              {showPriorityFilter && (
                <Select
                  type="multi"
                  options={maintenancePriorityOptions}
                  placeholder={t('maintenance:requests.filters.priorityFallback')}
                  size="small"
                  value={priority ?? []}
                  onChange={handlePriorityChange}
                  onReset={() => handlePriorityChange([])}
                />
              )}
              {showYardiPropertyFilter && (
                <Select
                  type="multi"
                  options={yardiPropertyOptions}
                  placeholder={t('maintenance:requests.filters.selectPhase')}
                  size="small"
                  value={yardiPropertyId ?? []}
                  onChange={handleYardiPropertyChange}
                  onReset={() => handleYardiPropertyChange([])}
                />
              )}
              {showResidentFilter && (
                <Select
                  searchable
                  type="multi"
                  options={(residentsOptions.data?.data ?? []).map((option) => ({
                    label: `${option.name} - (${option.count})`,
                    value: option.clientId,
                  }))}
                  dropdownOptions={{ width: ADDITIONAL_FILTERS_DROPDOWN_WIDTH }}
                  disabled={residentsOptions.isFetching}
                  placeholder={t('maintenance:requests.filters.residentPlaceholder')}
                  size="small"
                  value={filters.getFilter('clientId') ?? []}
                  onChange={handleResidentChange}
                  onReset={() => handleResidentChange([])}
                  onSearchChange={setResidentsSearch}
                />
              )}
            </>
          )
        }
        navigation={{
          actions: (
            <Button
              onlyBox
              size="small"
              use="secondary"
              title={t('maintenance:requests.csv')}
              disabled={isExporting || !requests.length}
              onClick={handleDownloadCSV}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <DatePickerInput
              mode="range"
              placeholder={t('maintenance:requests.dateRangePlaceholder')}
              size="small"
              value={dateRange}
              onRangeChange={handleDateChange}
            />
          ),
          search: !residentId && (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              placeholder={t('maintenance:requests.searchPlaceholder')}
              size="small"
              value={search}
              onChange={handleSearchChange}
            />
          ),
          statusTabs: (
            <StatusTabs options={statusTabsOptions} value={status} onChange={handleTabChange} />
          ),
        }}
        pagination={{
          paginationFilters: filters,
          // Note: without per-status counts (a BE before EL-15063) page by the overall total - it
          // can overshoot into empty trailing pages, but never hides rows.
          total: status === 'all' ? allCount : (statusCounts[status] ?? allCount),
        }}
        tableFilters={filters}
        sortBy={filters.getFilter('sortBy')}
        sortOrder={sortOrder}
        onSort={handleSort}
      />
      {isShowAddRequest && (
        <ModalContainer
          headerTitle={t('maintenance:page.createRequestModalTitle')}
          onClose={() => setShowAddRequest(false)}
        >
          <RequestMaintenanceForm
            toggleModal={() => setShowAddRequest(false)}
            lockResident={resident}
            lockBuilding={selectedBuilding}
            onSuccess={() => void refetch()}
          />
        </ModalContainer>
      )}
    </>
  )
}
